import React, { useCallback, useState } from "react";
import {
    FooterContainer,
    FooterText,
    FooterLink,
    FooterLanguageSelector,
    FooterBottom,
    Copyright,
    LanguageSwitcher,
    FooterHeadline,
    FooterButton,
    LanguageDropdown,
    LanguageOption,
} from "./footer.styles";
import { Row, Col } from "react-bootstrap";
import Logo from "../../../../assets/images/logo/logo-multicolor.svg";
import MaterialIcon from "../../../../components/material-icon";
import { colorPalette } from "../../../../utils/theme";
import useIsMobile from "../../../../hooks/useIsMobile";
import useCustomNavigate from "../../../../hooks/useCustomNavigate";
import useContactUsModalStore from "../../../../stores/contactUsModalStore";
import { usePWA } from "../../../../context/PWAContext";
import InstagramIcon from "../../../../assets/icons/instagram.svg";
import { useTranslation } from "react-i18next";
import { openExternalLink } from "../../../../utils/navigation";
import { Capacitor } from "@capacitor/core";
import useCookieConsentStore, { consentApplies } from "../../../../stores/cookieConsentStore";
import AndroidDownloadIcon from "../../../../assets/images/android_download.png";
import IOSDownloadIcon from "../../../../assets/images/ios_download.png";

// Sprachschalter aktivieren (temporär für Testing)
const isLanguageSwitcherEnabled = true; // process.env.REACT_APP_ENV !== 'production';

const Footer: React.FC = () => {
    const navigate = useCustomNavigate();
    const isMobile = useIsMobile();
    const isPWA = usePWA();
    const { i18n, t } = useTranslation();

    // Stores
    const openContactUs = useContactUsModalStore(state => state.openFor);
    const openCookieSettings = useCookieConsentStore(state => state.openSettings);

    // States
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

    const handleNavigation = useCallback((url: string) => {
        if (isPWA || Capacitor.isNativePlatform()) {
            navigate(url);
        } else {
            window.open(url, "_blank");
        }
    }, [isPWA, navigate]);

    // Sprachenwechsel-Funktion - nur aktiviert, wenn der Sprachschalter aktiviert ist
    const changeLanguage = (lng: string) => {
        localStorage.setItem('managd-user-language-preference', lng);
        i18n.changeLanguage(lng);
        setShowLanguageDropdown(false);
    };

    return (
        <FooterContainer>
            <Row>
                <Col xs={12} md={11} xxl={10} className="mx-auto">
                    <Row className="gy-4">
                        {/* Left Section */}
                        <Col xs={12} md={6}>
                            <img src={Logo} alt="Managd Logo" width={88} />
                            {!isMobile && (
                                <FooterText>
                                    Managd Property Management LLC
                                    <br /><br />
                                    1404, Ubora Office Tower, Business Bay
                                    <br />
                                    Dubai, United Arab Emirates
                                </FooterText>
                            )}
                        </Col>

                        {/* Right Section */}
                        <Col xs={12} md={6} className={isMobile ? "" : "d-flex justify-content-end"}>
                            <Row className={isMobile ? "gy-4" : "gx-4"}>
                                <Col xs={12} md={4}>
                                    <FooterHeadline>{t('footer.landlord')}</FooterHeadline>
                                    <FooterButton onClick={() => handleNavigation("/details#services")}>{t('footer.services')}</FooterButton>
                                    <FooterButton onClick={() => handleNavigation("/details#packages")}>{t('footer.packages')}</FooterButton>
                                    <FooterButton onClick={() => handleNavigation("/details#faq")}>{t('footer.faq')}</FooterButton>
                                    <FooterButton onClick={() => openContactUs("landlord")}>{t('footer.contact')}</FooterButton>
                                </Col>
                                <Col xs={12} md={8} className={isMobile ? "" : "ps-4"}>
                                    <FooterHeadline>{t('footer.tenant')}</FooterHeadline>
                                    <FooterButton onClick={() => handleNavigation("/tenant-home#available_units")}>{t('footer.available_units')}</FooterButton>
                                    <FooterButton onClick={() => handleNavigation("/tenant-home#how_it_works")}>{t('footer.how_it_works')}</FooterButton>
                                    <FooterButton onClick={() => handleNavigation("/tenant-home#faq")}>{t('footer.faq')}</FooterButton>
                                    <FooterButton onClick={() => openContactUs("tenant")}>{t('footer.contact')}</FooterButton>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    <div className="d-flex align-items-center gap-2 mt-3">
                        <img src={IOSDownloadIcon} alt="Managd Logo" width={100} style={{ cursor: "pointer" }} onClick={() => {
                            openExternalLink("https://apps.apple.com/de/app/managd/id6745959836");
                        }} />
                        <img src={AndroidDownloadIcon} alt="Managd Logo" width={110} style={{ cursor: "pointer" }} onClick={() => {
                            openExternalLink("https://play.google.com/store/apps/details?id=com.getmanagd.app");
                        }} />
                    </div>

                    {/* Language Selector & Social Media Icons */}
                    <FooterLanguageSelector>
                        {/* Language switcher nur anzeigen, wenn aktiviert */}
                        {isLanguageSwitcherEnabled && (
                            <LanguageSwitcher onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
                                <MaterialIcon icon="translate" size={14} color={colorPalette.textMain} />
                                <span>{i18n.language === 'de' ? 'Deutsch' : 'English'}</span>
                                <MaterialIcon icon="keyboard_arrow_down" size={14} color={colorPalette.textMain} />

                                {showLanguageDropdown && (
                                    <LanguageDropdown onClick={(e) => e.stopPropagation()}>
                                        {i18n.language !== 'de' && (
                                            <LanguageOption
                                                active={false}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    changeLanguage('de');
                                                }}
                                            >
                                                Deutsch
                                            </LanguageOption>
                                        )}
                                        {i18n.language !== 'en' && (
                                            <LanguageOption
                                                active={false}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    changeLanguage('en');
                                                }}
                                            >
                                                English
                                            </LanguageOption>
                                        )}
                                    </LanguageDropdown>
                                )}
                            </LanguageSwitcher>
                        )}

                        <img
                            src={InstagramIcon}
                            width={isMobile ? 16 : 20}
                            height={isMobile ? 16 : 20}
                            alt="Instagram Social Media"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                const instagramUsername = "getmanagd";
                                const instagramUrl = `https://www.instagram.com/${instagramUsername}`;
                                const instagramAppUrl = `instagram://user?username=${instagramUsername}`;

                                // Check if user is on a mobile device
                                if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                                    window.location.href = instagramAppUrl;
                                    setTimeout(() => {
                                        openExternalLink(instagramUrl);
                                    }, 1000);
                                } else {
                                    openExternalLink(instagramUrl);
                                }
                            }}
                        />
                    </FooterLanguageSelector>

                    {/* Bottom Section */}
                    <FooterBottom>
                        {isMobile ? (
                            <>
                                <div className="d-flex" style={{ gap: 8 }}>
                                    <FooterLink onClick={() => handleNavigation("/terms-and-conditions")}>{t('footer.terms')}</FooterLink>
                                    <FooterLink onClick={() => handleNavigation("/privacy-policy")}>{t('footer.privacy')}</FooterLink>
                                    {consentApplies() && (
                                        <FooterLink as="button" type="button" onClick={openCookieSettings}>{t('footer.cookie_settings')}</FooterLink>
                                    )}
                                </div>
                                <FooterText>
                                    {t('footer.copyright')}
                                    <br /><br />
                                    1404, Ubora Office Tower, Business Bay
                                    <br />
                                    Dubai, United Arab Emirates
                                </FooterText>
                            </>
                        ) : (
                            <>
                                <Copyright>
                                    {t('footer.copyright')}
                                </Copyright>
                                <FooterLink onClick={() => handleNavigation("/terms-and-conditions")}>{t('footer.terms')}</FooterLink>
                                <FooterLink onClick={() => handleNavigation("/privacy-policy")}>{t('footer.privacy')}</FooterLink>
                                {consentApplies() && (
                                    <FooterLink as="button" type="button" onClick={openCookieSettings}>{t('footer.cookie_settings')}</FooterLink>
                                )}
                            </>
                        )}
                    </FooterBottom>
                </Col>
            </Row>
        </FooterContainer>
    );
};

export default Footer;
